<template>
    <div class="line-chart">
        <h5 v-if="tableName">
            {{ tableName }}
        </h5>

        <span class="sub-header" v-if="subHeader">
			{{ subHeader }}
		</span>

        <span v-if="dataSet.length < 1">No record found</span>

        <section class="chart-container" v-else>
            <canvas :id="uniqueId" :height="dataType === 'stress' ? '80' : '110'"/>
        </section>
    </div>
</template>

<script>
import Chart from 'chart.js'

export default {
	props: {
		tableName: {
			type: String,
			default: ''
		},

		subHeader: {
			type: String,
			default: ''
		},

		dataType: {
			type: String,
			default: ''
		},

		labels: {
			type: Array,
			default: () => []
		},

		dataSet: {
			type: Array,
			default: () => []
		},

		color: {
			type: Array,
			default: () => []
		},

		emotionLevels: {
			type: Array,
			default: () => []
		},

		stressLevels: {
			type: Array,
			default: () => []
		}
	},

	data () {
		return {
			uniqueId: ''
		}
	},

	created () {
		this.generateUniqueId()
	},

	mounted () {
		if (this.dataSet.length > 0) this.renderLineChart()
	},

	methods: {
		/* Created Methods */
		generateUniqueId () {
			this.uniqueId = Math.random().toString(36).substr(2, 9)
		},

		/* Mounted Methods */
		renderLineChart () {
			const lineChartCanvas = document.getElementById(this.uniqueId)

			const data = this.setData()
			const options = this.setOptions()

			Chart.defaults.global.defaultFontColor = '#999'

			// eslint-disable-next-line
			const radarChart = new Chart(
				lineChartCanvas,
				{
					type: 'line',
					data: data,
					options: options
				}
			)
		},

		/* Set Data Methods */
		setData () {
			const data = {
				labels: this.setDataLabels(),

				datasets: this.setDataSets()
			}

			return data
		},

		setDataLabels () {
			const labels = this.labels.length === 1
				? ['', ...this.labels, '']
				: this.labels

			return labels
		},

		setDataSets () {
			const dataSet = [
				{
					data: this.setDataSetData(),
					borderColor: '#61DBFF',
					backgroundColor: 'rgba(177, 237, 255, 0.4)',
					pointBackgroundColor: 'rgba(62, 207, 250, 0.5)',
					fill: true
				}
			]

			if (this.dataType === 'stress') {
				dataSet[0].borderColor = '#fdea84'
				dataSet[0].backgroundColor = 'rgba(254, 246, 202, 0.4)'
				dataSet[0].pointBackgroundColor = 'rgba(252, 229, 104, 0.5)'
			}

			return dataSet
		},

		/* Set Option Methods */
		setOptions () {
			const option = {
				legend: {
					display: false
				},

				animation: {
					duration: 0
				},

				hover: {
					animationDuration: 0
				},

				responsiveAnimationDuration: 0,

				responsive: true,

				tooltips: {
					enabled: false
				},

				scales: this.setScales()
			}

			return option
		},

		setDataSetData () {
			return this.dataSet.length === 1
				? [{}, ...this.dataSet, {}]
				: this.dataSet
		},

		setScales () {
			const scales = {
				xAxes: [{
					display: true,

					ticks: {
						maxRotation: 45,

						minRotation: 45
					}
				}],

				yAxes: [{
					display: true,

					ticks: {
						min: 0,

						max: 11,

						stepSize: 1,

						suggestedMin: 0.5,

						suggestedMax: 5.5,

						callback: this.setYaxisLabel
					},

					gridLines: {
						display: true
					}
				}]
			}

			scales.yAxes[0].ticks.max = this.emotionLevels.length - 1

			if (this.dataType === 'stress') {
				scales.yAxes[0].ticks.max = 3
			}

			if (this.dataType === 'bpm') {
				scales.yAxes[0].ticks.max = 200
				scales.yAxes[0].ticks.stepSize = 50
			}

			return scales
		},

		setYaxisLabel (label) {
			if (this.dataType === 'bpm') return label

			if (this.dataType === 'stress') {
				return this.stressLevels[label]
			}

			return this.emotionLevels[label]
		}
	}
}
</script>

<style lang="scss" scoped>
.line-chart {
	padding-bottom: 25px;

    .sub-header {
		display: block;
		font-size: 13px;
		color: #5e5e5e;
		margin: 3px 0 7px 0;
	}

    .chart-legend {
        position: absolute;
        top: 70px;
        left: 0;

        .legend-item {
            display: flex;
            align-items: center;
            font-size: 13px;

            &:first-child {
                margin-bottom: 5px;
            }

            .legend-icon {
                display: inline-block;
                width: 23px;
                height: 10px;
                border-radius: 10px;
                margin-right: 10px;
                flex-shrink: 0;

                &.overall {
                    background: #d7d7fc;
                }

                &.user {
                    background: #3b86ff;
                }
            }
        }
    }

    .chart-container {
        width: 100%;
        margin: 0 auto;
    }
}
</style>
